import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, Input, Pagination, Select, Spin, Tooltip, Typography, Button } from 'antd';
import { DownOutlined, RadarChartOutlined, RightOutlined, SearchOutlined, SyncOutlined } from '@ant-design/icons';

const { Text } = Typography;

const TEXT_PRIMARY = '#1f1f1f';
const TEXT_MUTED = '#595959';
const PAGE_SIZE = 12;
const TREE_ARROW_WIDTH = 14;
const TREE_CHECKBOX_SLOT = 22;
const TREE_MONITOR_SLOT = 22;
const TREE_DEPTH_INDENT = 20;
const TREE_COLUMN_GAP = 8;

const TYPE_COLORS = {
  folder: '#1677ff',
  page: '#94167f',
};

const panelStyle = {
  flex: 1,
  minWidth: 280,
  border: '1px solid rgba(148, 22, 127, 0.25)',
  borderRadius: 8,
  background: '#faf8fb',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 360,
  maxHeight: 520,
};

const panelBodyStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '0 10px 4px',
};

const legendStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  display: 'flex',
  flexWrap: 'wrap',
  gap: '8px 14px',
  padding: '8px 10px',
  marginBottom: 0,
  background: '#fff',
  borderBottom: '1px solid rgba(148, 22, 127, 0.15)',
  borderRadius: '8px 8px 0 0',
  fontSize: 11,
  color: TEXT_MUTED,
};

const filterBarStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
  padding: '10px 10px 8px',
  borderBottom: '1px solid rgba(148, 22, 127, 0.1)',
  background: '#faf8fb',
};

const loadingPanelStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 280,
  padding: '40px 24px',
  border: '1px solid rgba(148, 22, 127, 0.2)',
  borderRadius: 8,
  background: 'linear-gradient(180deg, #ffffff 0%, #faf8fb 100%)',
  textAlign: 'center',
};

const sortTreeNodes = (nodes) => {
  nodes.sort((a, b) => {
    const typeRank = (a.type === 'folder' ? 0 : 1) - (b.type === 'folder' ? 0 : 1);
    if (typeRank !== 0) return typeRank;
    return String(a.title || '').localeCompare(String(b.title || ''));
  });
  nodes.forEach((node) => sortTreeNodes(node.children));
};

const buildPageTree = (pages) => {
  const byId = new Map(
    pages.map((page) => [page.id, { ...page, children: [] }]),
  );
  const roots = [];
  pages.forEach((page) => {
    const node = byId.get(page.id);
    const parentId = page.parent_id;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  sortTreeNodes(roots);
  return roots;
};

const filterTree = (nodes, predicate) => (
  nodes.reduce((acc, node) => {
    const filteredChildren = filterTree(node.children, predicate);
    if (predicate(node) || filteredChildren.length) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, [])
);

const flattenTree = (nodes) => {
  const rows = [];
  const walk = (list) => {
    list.forEach((node) => {
      rows.push(node);
      walk(node.children);
    });
  };
  walk(nodes);
  return rows;
};

const collectDescendantIds = (id, childrenByParent) => {
  const result = [];
  const queue = [...(childrenByParent.get(id) || [])];
  while (queue.length) {
    const next = queue.shift();
    result.push(next);
    queue.push(...(childrenByParent.get(next) || []));
  }
  return result;
};

const expandRootSelection = (rootIds, childrenByParent) => {
  const selected = new Set();
  (rootIds || []).forEach((id) => {
    const key = String(id || '').trim();
    if (!key) return;
    selected.add(key);
    collectDescendantIds(key, childrenByParent).forEach((childId) => selected.add(childId));
  });
  return selected;
};

const minimalRootIds = (selectedIds, pagesById) => {
  const selected = new Set(selectedIds);
  return [...selected]
    .filter((id) => {
      let current = pagesById.get(id);
      while (current?.parent_id) {
        if (selected.has(current.parent_id)) return false;
        current = pagesById.get(current.parent_id);
      }
      return true;
    })
    .sort();
};

const ConfluencePageScopePicker = ({
  spaceKey,
  value = [],
  onChange,
  fetchPageTree,
  disabled = false,
  selectedPanelFooter = null,
  monitoredPageIds = null,
  onToggleMonitor = null,
  onCheckMonitoredUpdates = null,
  monitorCheckLoading = false,
  monitoredPageCount = 0,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [allPages, setAllPages] = useState([]);
  const [treeLoaded, setTreeLoaded] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState('__all__');
  const [browsePage, setBrowsePage] = useState(1);
  const [selectedPage, setSelectedPage] = useState(1);
  const [collapsedBrowse, setCollapsedBrowse] = useState(() => new Set());
  const [collapsedSelected, setCollapsedSelected] = useState(() => new Set());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const loadSeqRef = useRef(0);
  const valueKey = useMemo(
    () => (Array.isArray(value) ? [...value].map(String).sort().join('|') : ''),
    [value],
  );

  const pagesById = useMemo(() => {
    const map = new Map();
    allPages.forEach((page) => map.set(page.id, page));
    return map;
  }, [allPages]);

  const childrenByParent = useMemo(() => {
    const map = new Map();
    allPages.forEach((page) => {
      const parentId = page.parent_id;
      if (!parentId) return;
      if (!map.has(parentId)) map.set(parentId, []);
      map.get(parentId).push(page.id);
    });
    return map;
  }, [allPages]);

  const emitChange = useCallback((nextSelectedSet) => {
    const roots = minimalRootIds([...nextSelectedSet], pagesById);
    setSelectedIds(nextSelectedSet);
    onChange?.(roots, {
      allSelectedIds: [...nextSelectedSet],
      pages: allPages,
      roots,
    });
  }, [allPages, onChange, pagesById]);

  const loadTree = useCallback(async () => {
    if (!spaceKey || !fetchPageTree) return;
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError('');
    setTreeLoaded(false);
    try {
      const res = await fetchPageTree(spaceKey);
      if (seq !== loadSeqRef.current) return;
      const pages = (res?.pages || res?.data?.pages || []).map((page) => ({
        id: String(page.id || '').trim(),
        title: String(page.title || 'Untitled'),
        type: String(page.type || 'page').toLowerCase() === 'folder' ? 'folder' : 'page',
        parent_id: page.parent_id ? String(page.parent_id) : null,
        child_count: typeof page.child_count === 'number' ? page.child_count : 0,
        web_url: page.web_url || '',
      })).filter((page) => page.id);
      setAllPages(pages);
      const localChildren = new Map();
      pages.forEach((page) => {
        if (!page.parent_id) return;
        if (!localChildren.has(page.parent_id)) localChildren.set(page.parent_id, []);
        localChildren.get(page.parent_id).push(page.id);
      });
      const savedRoots = Array.isArray(value) ? value.map(String) : [];
      setSelectedIds(expandRootSelection(savedRoots, localChildren));
      setTreeLoaded(true);
      setBrowsePage(1);
      setSelectedPage(1);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load Confluence pages.';
      setError(String(detail));
      setAllPages([]);
      setSelectedIds(new Set());
      setTreeLoaded(true);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [fetchPageTree, spaceKey, value]);

  useEffect(() => {
    void loadTree();
    // Reload when space changes; value is applied after load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceKey, fetchPageTree]);

  useEffect(() => {
    if (!treeLoaded || loading || !allPages.length) return;
    const next = expandRootSelection(
      valueKey ? valueKey.split('|') : [],
      childrenByParent,
    );
    const currentKey = [...selectedIds].sort().join('|');
    const nextKey = [...next].sort().join('|');
    if (currentKey !== nextKey) {
      setSelectedIds(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueKey, treeLoaded, allPages.length]);

  const pageMatchesSearch = useCallback((page, search) => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return true;
    return (
      page.id.toLowerCase().includes(q)
      || page.title.toLowerCase().includes(q)
      || page.type.toLowerCase().includes(q)
    );
  }, []);

  const pageMatchesFilter = useCallback((page) => {
    if (typeFilter !== '__all__' && page.type !== typeFilter) return false;
    return pageMatchesSearch(page, searchText);
  }, [pageMatchesSearch, searchText, typeFilter]);

  const fullTree = useMemo(() => buildPageTree(allPages), [allPages]);

  const browseTree = useMemo(
    () => filterTree(fullTree, pageMatchesFilter),
    [fullTree, pageMatchesFilter],
  );

  const browseFlat = useMemo(() => flattenTree(browseTree), [browseTree]);

  const selectedPages = useMemo(
    () => allPages.filter((page) => selectedIds.has(page.id)),
    [allPages, selectedIds],
  );

  const selectedTree = useMemo(() => buildPageTree(selectedPages), [selectedPages]);

  const hasSelectedAncestor = useCallback((id) => {
    let current = pagesById.get(id);
    while (current?.parent_id) {
      if (selectedIds.has(current.parent_id)) return true;
      current = pagesById.get(current.parent_id);
    }
    return false;
  }, [pagesById, selectedIds]);

  const addWithDescendants = (set, id) => {
    set.add(id);
    collectDescendantIds(id, childrenByParent).forEach((childId) => set.add(childId));
  };

  const removeWithDescendants = (set, id) => {
    set.delete(id);
    collectDescendantIds(id, childrenByParent).forEach((childId) => set.delete(childId));
  };

  const togglePageId = (id, checked) => {
    if (!checked && hasSelectedAncestor(id)) return;
    const next = new Set(selectedIds);
    if (checked) addWithDescendants(next, id);
    else removeWithDescendants(next, id);
    emitChange(next);
  };

  const toggleAllVisible = (checked) => {
    const next = new Set(selectedIds);
    if (checked) {
      browseTree.forEach((root) => {
        if (!hasSelectedAncestor(root.id)) addWithDescendants(next, root.id);
      });
    } else {
      browseFlat.forEach((node) => {
        if (!hasSelectedAncestor(node.id)) removeWithDescendants(next, node.id);
      });
    }
    emitChange(next);
  };

  const toggleCollapsed = (scope, id) => {
    const setter = scope === 'browse' ? setCollapsedBrowse : setCollapsedSelected;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderColorLegend = () => (
    <div style={legendStyle}>
      <Text style={{ fontSize: 11, color: TEXT_MUTED, marginRight: 4 }}>Type colors:</Text>
      {Object.entries(TYPE_COLORS).map(([name, color]) => (
        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: color,
              display: 'inline-block',
            }}
          />
          <span style={{ color: TEXT_PRIMARY, textTransform: 'capitalize' }}>{name}</span>
        </span>
      ))}
    </div>
  );

  const renderPageLabel = (node, { muted = false, viaParent = false } = {}) => (
    <span
      style={{
        fontSize: 12,
        lineHeight: 1.5,
        color: muted ? TEXT_MUTED : TEXT_PRIMARY,
        display: 'block',
        paddingTop: 2,
      }}
    >
      <span style={{ color: TYPE_COLORS[node.type] || TEXT_PRIMARY, fontWeight: muted ? 500 : 600 }}>
        {node.title}
      </span>
      <Text style={{ marginLeft: 6, fontSize: 11, color: TEXT_MUTED }}>
        ({node.type}{viaParent ? ', via parent' : ''})
      </Text>
    </span>
  );

  const renderCheckboxSlot = (node, { isSelected, ancestorSelected }) => (
    <Checkbox
      checked={isSelected}
      disabled={disabled || loading || ancestorSelected}
      onChange={(e) => togglePageId(node.id, e.target.checked)}
      style={{ margin: 0, padding: 0, lineHeight: 1 }}
    />
  );

  const renderMonitorSlot = (node) => {
    if (!onToggleMonitor) return null;
    const active = monitoredPageIds?.has?.(node.id);
    return (
      <Tooltip title={active ? 'Stop monitoring for Confluence updates' : 'Monitor for Confluence content updates'}>
        <span
          role="button"
          tabIndex={disabled || loading ? -1 : 0}
          aria-label={active ? `Stop monitoring ${node.title}` : `Monitor ${node.title} for updates`}
          aria-pressed={active}
          onClick={(e) => {
            e.stopPropagation();
            if (disabled || loading) return;
            onToggleMonitor(node.id, !active);
          }}
          onKeyDown={(e) => {
            if (disabled || loading) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onToggleMonitor(node.id, !active);
            }
          }}
          style={{
            width: TREE_MONITOR_SLOT,
            height: 16,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: disabled || loading ? 'not-allowed' : 'pointer',
            color: active ? '#1677ff' : TEXT_MUTED,
            background: active ? 'rgba(22, 119, 255, 0.14)' : 'transparent',
            border: active ? '1px solid rgba(22, 119, 255, 0.45)' : '1px solid transparent',
            borderRadius: 4,
            opacity: disabled || loading ? 0.45 : 1,
            lineHeight: 1,
          }}
        >
          <RadarChartOutlined style={{ fontSize: 13, fontWeight: active ? 700 : 400 }} />
        </span>
      </Tooltip>
    );
  };

  const renderTreeNode = (node, { scope, depth = 0 }) => {
    const hasChildren = node.children.length > 0;
    const collapsedSet = scope === 'browse' ? collapsedBrowse : collapsedSelected;
    const expanded = hasChildren && !collapsedSet.has(node.id);
    const ancestorSelected = hasSelectedAncestor(node.id);
    const isSelected = selectedIds.has(node.id);
    const viaParent = ancestorSelected;
    const mutedLabel = ancestorSelected;
    const showMonitor = scope === 'selected' && onToggleMonitor;

    return (
      <div key={node.id}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: showMonitor
              ? `${TREE_ARROW_WIDTH}px ${TREE_CHECKBOX_SLOT}px ${TREE_MONITOR_SLOT}px minmax(0, 1fr)`
              : `${TREE_ARROW_WIDTH}px ${TREE_CHECKBOX_SLOT}px minmax(0, 1fr)`,
            columnGap: TREE_COLUMN_GAP,
            alignItems: 'start',
            marginBottom: 4,
            paddingLeft: depth * TREE_DEPTH_INDENT,
          }}
        >
          <span
            style={{
              width: TREE_ARROW_WIDTH,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 5,
            }}
          >
            {hasChildren ? (
              <span
                role="button"
                tabIndex={0}
                onClick={() => toggleCollapsed(scope, node.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleCollapsed(scope, node.id);
                  }
                }}
                style={{ cursor: 'pointer', color: TYPE_COLORS[node.type], lineHeight: 1 }}
              >
                {expanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
              </span>
            ) : null}
          </span>
          <span
            style={{
              width: TREE_CHECKBOX_SLOT,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 2,
            }}
          >
            {renderCheckboxSlot(node, { isSelected, ancestorSelected })}
          </span>
          {showMonitor ? (
            <span
              style={{
                width: TREE_MONITOR_SLOT,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 2,
              }}
            >
              {renderMonitorSlot(node)}
            </span>
          ) : null}
          <span style={{ minWidth: 0, paddingTop: 1 }}>
            {renderPageLabel(node, { muted: mutedLabel, viaParent })}
          </span>
        </div>
        {hasChildren && expanded
          ? node.children.map((child) => renderTreeNode(child, { scope, depth: depth + 1 }))
          : null}
      </div>
    );
  };

  const browseSelectionState = useMemo(() => {
    const selectableRoots = browseTree.filter((root) => !hasSelectedAncestor(root.id));
    if (!selectableRoots.length) return { checked: false, indeterminate: false };
    const fullySelected = selectableRoots.filter((root) => selectedIds.has(root.id)).length;
    return {
      checked: fullySelected === selectableRoots.length,
      indeterminate: fullySelected > 0 && fullySelected < selectableRoots.length,
    };
  }, [browseTree, hasSelectedAncestor, selectedIds]);

  const paginateRoots = (roots, page) => {
    const start = (page - 1) * PAGE_SIZE;
    return roots.slice(start, start + PAGE_SIZE);
  };

  const browsePageRoots = paginateRoots(browseTree, browsePage);
  const selectedPageRoots = paginateRoots(selectedTree, selectedPage);
  const selectedRootCount = minimalRootIds([...selectedIds], pagesById).length;

  const renderPagination = (total, page, onPageChange) => (
    <div style={{ padding: '8px 10px', borderTop: '1px solid rgba(148, 22, 127, 0.1)', background: '#faf8fb' }}>
      <Pagination
        size="small"
        current={page}
        pageSize={PAGE_SIZE}
        total={total}
        onChange={onPageChange}
        showSizeChanger={false}
        showTotal={(t, range) => `${range[0]}-${range[1]} of ${t} roots`}
        disabled={disabled || loading}
      />
    </div>
  );

  return (
    <div>
      {loading ? (
        <div style={loadingPanelStyle}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16, fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY }}>
            Loading Confluence pages
          </Text>
          <Text style={{ display: 'block', marginTop: 6, fontSize: 13, color: TEXT_MUTED, maxWidth: 360, lineHeight: 1.55 }}>
            Fetching nested folders and pages for space {spaceKey}…
          </Text>
        </div>
      ) : (
        <>
          {error ? (
            <div style={{ marginBottom: 8, color: '#cf1322', fontSize: 13 }}>{error}</div>
          ) : null}

          {allPages.length > 0 ? renderColorLegend() : null}

          <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={panelStyle}>
              <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
                Browse pages ({allPages.length})
              </div>
              <Text style={{ display: 'block', padding: '2px 10px 6px', fontSize: 11, color: TEXT_MUTED }}>
                Nested like the Confluence sidebar. Select a folder or page to include its children automatically.
              </Text>
              <div style={filterBarStyle}>
                <Select
                  size="small"
                  value={typeFilter}
                  disabled={disabled || loading}
                  onChange={setTypeFilter}
                  style={{ minWidth: 140, flex: '1 1 140px' }}
                  options={[
                    { value: '__all__', label: 'All types' },
                    { value: 'folder', label: 'Folder' },
                    { value: 'page', label: 'Page' },
                  ]}
                />
                <Input
                  allowClear
                  size="small"
                  prefix={<SearchOutlined style={{ color: TEXT_MUTED }} />}
                  placeholder="Search titles…"
                  value={searchText}
                  disabled={disabled || loading}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setBrowsePage(1);
                  }}
                  style={{ flex: '1 1 200px' }}
                />
              </div>
              <div style={{ padding: '0 10px 6px' }}>
                <Checkbox
                  checked={browseSelectionState.checked}
                  indeterminate={browseSelectionState.indeterminate}
                  disabled={disabled || loading || !browseTree.length}
                  onChange={(e) => toggleAllVisible(e.target.checked)}
                  style={{ color: TEXT_PRIMARY, fontSize: 12 }}
                >
                  Select all visible ({browseFlat.length})
                </Checkbox>
              </div>
              <div style={panelBodyStyle}>
                {browsePageRoots.length ? (
                  browsePageRoots.map((node) => renderTreeNode(node, { scope: 'browse' }))
                ) : treeLoaded ? (
                  <div style={{ padding: '8px 4px' }}>
                    <Text style={{ display: 'block', color: TEXT_PRIMARY, fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
                      {allPages.length === 0 ? 'No pages found' : 'No matching pages'}
                    </Text>
                    <Text style={{ color: TEXT_MUTED, fontSize: 12, lineHeight: 1.55 }}>
                      {allPages.length === 0
                        ? 'This space has no folders or pages to sync.'
                        : 'Try a different search or type filter.'}
                    </Text>
                  </div>
                ) : null}
              </div>
              {browseTree.length > PAGE_SIZE
                ? renderPagination(browseTree.length, browsePage, setBrowsePage)
                : null}
            </div>

            <div style={panelStyle}>
              <div style={{ fontWeight: 600, padding: '10px 10px 0', color: TEXT_PRIMARY }}>
                Selected pages ({selectedRootCount})
              </div>
              <Text style={{ display: 'block', padding: '4px 10px 8px', fontSize: 11, color: TEXT_MUTED }}>
                Nested by parent-child hierarchy. Click the arrow to expand or collapse.
                {onToggleMonitor ? ' Use the monitor icon to watch for Confluence content changes.' : ''}
              </Text>
              <div style={panelBodyStyle}>
                {selectedPageRoots.length ? (
                  selectedPageRoots.map((node) => renderTreeNode(node, { scope: 'selected' }))
                ) : (
                  <Text style={{ color: TEXT_MUTED, fontSize: 12 }}>No pages selected yet</Text>
                )}
              </div>
              {selectedTree.length > PAGE_SIZE
                ? renderPagination(selectedTree.length, selectedPage, setSelectedPage)
                : null}
              {(selectedPanelFooter || onCheckMonitoredUpdates) ? (
                <div
                  style={{
                    padding: '8px 10px',
                    borderTop: '1px solid rgba(148, 22, 127, 0.1)',
                    background: '#faf8fb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  {onCheckMonitoredUpdates ? (
                    <Tooltip
                      title={
                        monitoredPageCount > 0
                          ? 'Compare monitored pages against Confluence now'
                          : 'Enable monitoring on at least one page first'
                      }
                    >
                      <Button
                        size="small"
                        icon={<SyncOutlined />}
                        loading={monitorCheckLoading}
                        disabled={disabled || monitoredPageCount < 1}
                        onClick={() => onCheckMonitoredUpdates()}
                      >
                        Check updates
                      </Button>
                    </Tooltip>
                  ) : <span />}
                  {selectedPanelFooter}
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ConfluencePageScopePicker;
